// Polling utility for async backend workflows (crawl / generate / run-all).
// The backend returns a `poll_url`; we poll a fetch function until a terminal
// state is reached. Supports the running / completed / failed lifecycle.

export function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Repeatedly call `fn` until `isDone` returns true (resolves with the result),
 * `isFailed` returns true (rejects), or `timeout` is exceeded (rejects).
 *
 * @param {() => Promise<any>} fn       fetch function returning the latest state
 * @param {object} opts
 * @param {(r:any)=>boolean} opts.isDone    terminal "completed" predicate
 * @param {(r:any)=>boolean} [opts.isFailed] terminal "failed" predicate
 * @param {(r:any)=>void}    [opts.onTick]   called with every poll result
 * @param {number} [opts.interval=1500] ms between polls
 * @param {number} [opts.timeout=600000] ms before giving up
 */
export async function pollUntil(fn, opts = {}) {
  const {
    isDone = () => true,
    isFailed,
    onTick,
    interval = 1500,
    timeout = 600000,
  } = opts;

  const start = Date.now();

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const result = await fn();
    if (onTick) onTick(result);
    if (isFailed && isFailed(result)) {
      throw new Error("The workflow reported a failure.");
    }
    if (isDone(result)) return result;
    if (Date.now() - start > timeout) {
      throw new Error("Timed out waiting for the workflow to finish.");
    }
    await delay(interval);
  }
}
